import React, { useState, useEffect, Fragment, useRef } from "react"
import StickyBox from "react-sticky-box"
import md5 from "js-md5"
import debounce from "lodash/debounce"

import { getServiceSection, getServiceSectionsPreview } from "../apis/VenuesAPI"

import CartSummaryModal from "../CartSummaryModal"
import AddServiceModal from "./components/modal/AddServiceModal"
import SectionsList from "./SectionsList"
import ShowSearchResults from "./ShowSearchResults"
import ServicesSection from "./ServicesSection"
import InlineSectionsFilter from "./InlineSectionsFilter"
import ServicesSearch from "./ServicesSearch"
import ServicesSectionsPreview from "./ServicesSectionsPreview"

import CartStateStore, {
  ADD_TO_CART,
  CHANGE_STORE,
  useCartStateStore,
} from "../stores/CartStateStore"

import style from "./index.module.scss"

// ----------------------------------------------------------------------------

export const ALL_CATEGORIES = "all-categories"

function getInitialState() {
  return {
    service: {
      name: "",
      description: "",
    },
    serviceSectionPartConfigs: [],
    showModal: false,
  }
}

const sectionsCache = {}

const SEARCH_TIME_TO_WAIT = 1000

function _VenueServicesLis(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const {
    venue,
    deliveryAddress,
    selectedServiceId = undefined,
    selectedServiceSection = undefined,
  } = props

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const venueServicesTopRef = useRef(null)
  const [serviceState, setServiceState] = useState(getInitialState())
  const [queryState, setQueryState] = useState(null)
  const [sectionState, setSectionState] = useState(null)
  const [sortFunction, setSortFunction] = useState(null)

  const [showAddedItem, setShowAddedItem] = useState(0)
  const [cartState, dispatchCartState] = useCartStateStore()

  const [sectionsPreview, setSectionsPreview] = useState([])

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    dispatchCartState({ type: CHANGE_STORE, payload: venue })

    if (!venue.ordering_enabled) {
      alert(I18n.t("service_ordering.sorry_restaurant_unavailable"))
    }

    getServiceSectionsPreview(venue.id)
      .then((res) => {
        setSectionsPreview(res)
      })
      .catch()

    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  useEffect(() => {
    function handleSectionLoad(section) {
      // console.log("Section loaded from server ", section)

      setSectionState(section)
      sectionsCache[section.id] = section
    }

    // GUARD
    if (
      (sectionState?.id || ALL_CATEGORIES) === ALL_CATEGORIES &&
      !selectedServiceId
    )
      return

    let sectionId = sectionState?.id
      ? sectionState.id
      : selectedServiceSection || (venue.service_sections[0] || {}).id

    // Check if the section is already present in the cache
    // @todo: Check if the cache grow too much in terms of memory?
    if (sectionsCache[sectionId]) {
      // console.log("Load from cache")

      setSectionState(sectionsCache[sectionId])
    } else {
      // console.log("Load from server")

      getServiceSection(sectionId)
        .then((data) => {
          if (!sectionState) {
            setupCurrentService(data)
          }
          handleSectionLoad(data)
        })
        .catch((err) => {
          // TODO: handle
        })
    }
  }, [sectionState])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function handleScroll() {
    let toast = document.getElementById("add-to-cart-toast")
    toast.style.top = `${
      100 - (window.pageYOffset > 80 ? 80 : window.pageYOffset)
    }px`
  }

  function setupCurrentService(data) {
    if (selectedServiceId) {
      let sections = getAvailableSections()
      let service = undefined
      let serviceSection = undefined
      for (const section of sections) {
        service = data.services.find((s) => s.id === selectedServiceId)
        serviceSection = section
        if (service) {
          break
        }
      }
      let configParts = serviceSection.service_section_part_configs || []

      if (service && serviceSection) {
        handleShowModal(service, configParts)
      }
    }
  }

  function getAvailableSections() {
    const today = new Date().getDay().toString()

    return venue.service_sections.filter((section) => {
      const unavailable_days = section.unavailable_days || []

      return unavailable_days.indexOf(today) === -1
    })
  }

  function handleSearch(query) {
    setQueryState(query)
  }

  function handleShowModal(service, serviceSectionPartConfigs) {
    setServiceState({
      ...serviceState,
      service: service,
      serviceSectionPartConfigs: serviceSectionPartConfigs,
      showModal: true,
    })
  }

  function handleSectionClick(section) {
    // TODO: better state management
    setQueryState(null)
    setSectionState(section)

    // GUARD
    if (!venueServicesTopRef.current) {
      return
    }
    // Lets the services list to scroll to top when a section is selected
    // sort of hack to have scrollIntoView keep a top offset (leaving space to top bar)
    let pos = venueServicesTopRef.current.style.position
    let top = venueServicesTopRef.current.style.top
    venueServicesTopRef.current.style.position = "relative"
    venueServicesTopRef.current.style.top = "-85px"
    venueServicesTopRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
    venueServicesTopRef.current.style.top = top
    venueServicesTopRef.current.style.position = pos
  }

  function handleModalHide() {
    setServiceState(getInitialState())
  }

  function onProductAdded() {
    setShowAddedItem(1)

    setTimeout(() => {
      setShowAddedItem(0)
    }, 2000)
  }

  function handleAddToCart(additionalParts, quantity, specialInstructions) {
    if (!venue.ordering_enabled) {
      alert(I18n.t("service_ordering.cannot_add_to_cart"))
      return
    }

    onProductAdded()

    let sku = serviceState.service.id
    const name = serviceState.service.name
    const section_title = serviceState.service.section_title
    let total_price = serviceState.service.price_cents

    if (additionalParts) {
      additionalParts.forEach((part) => {
        sku += "-" + part.id
        total_price += part.price
      })
    }

    sku += specialInstructions || ""
    sku = md5(sku)

    const update = {
      id: serviceState.service.id,
      name: name,
      quantity: 1,
      original_price_cents: serviceState.service.original_price_cents,
      price_cents: serviceState.service.price_cents,
      additional_parts: additionalParts,
      section_title: section_title,
      special_instructions: specialInstructions,
    }

    dispatchCartState({
      type: ADD_TO_CART,
      payload: { sku: sku, update: update, quantity: quantity },
    })

    setServiceState({ ...serviceState, showModal: false })
  }

  function handleOrderBySort(sortElement) {
    setSortFunction(sortElement)
  }

  // ----------------------------------------------------------------------------
  // Render Functions
  // ----------------------------------------------------------------------------

  // -------------------------------------
  // Component local variables
  // -------------------------------------
  const featuredServices = []
  venue.service_sections?.forEach((section) => {
    section?.services?.forEach((service) => {
      if (!!service.featured) {
        featuredServices.push(service)
      }
    })
  })

  // TODO: featured services
  // const featuredServices = []
  // venue.service_sections?.forEach((section) => {
  //   section?.services?.forEach((service) => {
  //     if (!!service.featured) {
  //       featuredServices.push(service)
  //     }
  //   })
  // })

  const searchHandler = debounce(handleSearch, SEARCH_TIME_TO_WAIT)

  // -------------------------------------

  return (
    <Fragment>
      <div className={`container-fluid p-0 ${style.sectionListContainer}`}>
        <div className={style.layoutContainer}>
          <StickyBox
            offsetTop={50}
            className={`hidden-xs col-md-4 ${style.stickyBox}`}
          >
            <div className={`${style.filteringContainer}`}>
              <ServicesSearch handleSearch={searchHandler} hasTitle={false} />
              <SectionsList
                selectedSection={sectionState || { id: ALL_CATEGORIES }}
                sections={venue.service_sections}
                onClick={handleSectionClick}
              />
            </div>
          </StickyBox>

          <div className={style.contentContainer}>
            <InlineSectionsFilter
              className="visible-xs"
              handleSearch={handleSearch}
              selectedSection={sectionState || { id: ALL_CATEGORIES }}
              sections={venue.service_sections}
              sortElement={sortFunction}
              onSectionClick={handleSectionClick}
              onOrderByClick={handleOrderBySort}
            />
            {queryState && (
              <ShowSearchResults
                query={queryState}
                venueId={venue.id}
                orderingEnabled={venue.ordering_enabled}
                onServiceClick={handleShowModal}
                handleSort={handleOrderBySort}
                sortFunction={sortFunction}
              />
            )}

            <div ref={venueServicesTopRef}>
              {!queryState &&
                (sectionState?.id || ALL_CATEGORIES) !== ALL_CATEGORIES && (
                  <ServicesSection
                    sectionState={sectionState}
                    onServiceClick={handleShowModal}
                    handleSort={handleOrderBySort}
                    sortFunction={sortFunction}
                  />
                )}
              {!queryState &&
                (sectionState?.id || ALL_CATEGORIES) === ALL_CATEGORIES && (
                  <ServicesSectionsPreview
                    sectionsPreview={sectionsPreview}
                    sections={venue.service_sections}
                    onServiceClick={handleShowModal}
                    handleSort={handleOrderBySort}
                    handleShowAll={handleSectionClick}
                  />
                )}
            </div>
          </div>
        </div>

        <AddServiceModal
          show={serviceState.showModal}
          onHide={handleModalHide}
          addToCart={handleAddToCart}
          service={serviceState.service}
          serviceSectionPartConfigs={serviceState.serviceSectionPartConfigs}
        />

        <CartSummaryModal
          dispatchCartState={dispatchCartState}
          cartState={cartState}
          orderingEnabled={venue.ordering_enabled}
          latitude={deliveryAddress.latitude}
          longitude={deliveryAddress.longitude}
          minGoodsPrice={venue.min_goods_price_cents}
        />
      </div>

      <div
        id="add-to-cart-toast"
        className={style.itemAdded}
        style={{ opacity: showAddedItem }}
      >
        <i className="fal fa-shopping-cart"></i>
        <span className={style.added}>{I18n.t("add_cart")}</span>
      </div>
    </Fragment>
  )
}

// ----------------------------------------------------------------------------

export default function VenueServicesList(props) {
  return (
    <CartStateStore>
      <_VenueServicesLis {...props}></_VenueServicesLis>
    </CartStateStore>
  )
}
